/**
 * Direct, un-wrapped calls to the API's /auth/* endpoints.
 *
 * These bypass the generic apiFetch wrapper deliberately — apiFetch's 401
 * interceptor would call back into postRefresh() and create a recursion.
 * The auth endpoints are the only place that touches /auth/refresh.
 */
import type { AuthTokens, AuthUser } from './types';

const API_BASE = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '');

export interface AuthSuccessResponse {
  status: number;
  data: {
    user: AuthUser;
    tokens: AuthTokens;
  };
}

export class AuthApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = 'AuthApiError';
    this.status = status;
  }
}

/**
 * POST /auth/refresh — sends the httpOnly refreshToken cookie, gets back a fresh
 * access+identity pair, and re-sets the rotated refreshToken + la_session cookies
 * via Set-Cookie. The caller pushes the new accessToken into the tokenStore.
 */
export async function postRefresh(signal?: AbortSignal): Promise<AuthSuccessResponse> {
  if (!API_BASE) throw new AuthApiError(0, 'VITE_API_BASE_URL is not configured');
  const res = await fetch(`${API_BASE}/auth/refresh`, {
    method: 'POST',
    credentials: 'include',
    signal,
  });
  if (!res.ok) throw new AuthApiError(res.status, `refresh failed (${res.status})`);
  return (await res.json()) as AuthSuccessResponse;
}

/**
 * POST /auth/logout — best-effort; the caller always clears local state
 * regardless of the response. The API revokes every active refresh token and
 * clears both auth cookies.
 */
export async function postLogout(accessToken: string): Promise<void> {
  if (!API_BASE) return;
  try {
    await fetch(`${API_BASE}/auth/logout`, {
      method: 'POST',
      credentials: 'include',
      headers: { Authorization: `Bearer ${accessToken}` },
    });
  } catch {
    /* swallow — caller clears local state regardless */
  }
}
